import type { UIMessage } from "ai"
import { defaultProject, fromDict, type Project } from "./core/state"
import { PHASES, type Phase } from "./core/phases"
import type { MenuOption, RenderedBlock, RenderedQuilt, RenderedThumbnail } from "./tools"

// Storage layout
// ──────────────
// Single source of truth for the active session: one LoomDoc record in
// DOC_STORE under KEY="current". Project, transcript, and tool-output caches
// are persisted together in a single IndexedDB transaction so a refresh can
// only ever observe a coherent snapshot — never a project that disagrees with
// its transcript or its rendered visualizations.
//
// Saved/named projects (PROJECTS_STORE) embed the same LoomDoc shape so Open
// restores the visualizations and pending menu rows alongside the project.
//
// PINS_STORE holds the user's scrapbook of pinned artifacts — block and
// quilt snapshots that can be opened as new projects. Pin records use a
// content hash for the IDB key so re-pinning the same design is a no-op.
//
// LEGACY_* stores are read once on first load to migrate users from the
// previous three-store layout, then ignored.

const DB_NAME = "loom"
const DOC_STORE = "doc"
const PROJECTS_STORE = "projects"
export const PINS_STORE = "pins"

const LEGACY_PROJECT_STORE = "project"
const LEGACY_MESSAGES_STORE = "messages"
const LEGACY_TOOL_OUTPUTS_STORE = "tool_outputs"

const KEY = "current"

export interface ToolOutputs {
  blockSvgs: Record<string, RenderedBlock>
  diagramSvgs: Record<string, RenderedBlock>
  quiltSvgs: Record<string, RenderedQuilt>
  quiltGrids: Record<string, RenderedThumbnail[]>
  planMarkdowns: Record<string, string>
  pendingOptions: MenuOption[]
}

export const EMPTY_TOOL_OUTPUTS: ToolOutputs = {
  blockSvgs: {},
  diagramSvgs: {},
  quiltSvgs: {},
  quiltGrids: {},
  planMarkdowns: {},
  pendingOptions: [],
}

export interface LoomDoc {
  project: Project
  transcript: UIMessage[]
  toolOutputs: ToolOutputs
  schemaVersion: number
  // User-chosen phase, or null to follow derivation (resolvePhase in
  // core/phases.ts). The chat chrome uses the resolved phase to scope the
  // menu and the headline affordance; the LLM does not see this field.
  phase?: Phase | null
  // Optional intent label — set when the session was materialized from a
  // block pin ("block") or a quilt pin ("quilt"). Travels with the doc so
  // re-saving preserves it. Absent for fresh docs created via "New project"
  // and for legacy docs that pre-date the pin feature; the project switcher
  // derives a kind from layout state in those cases.
  kind?: DocKind
}

export function emptyDoc(): LoomDoc {
  return {
    project: defaultProject(),
    transcript: [],
    toolOutputs: EMPTY_TOOL_OUTPUTS,
    schemaVersion: 1,
    phase: null,
  }
}

// Shared DB connection opener — exported so sibling modules (pins.ts) can
// reuse the same DB and version without drift.
export function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 6)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(DOC_STORE)) db.createObjectStore(DOC_STORE)
      if (!db.objectStoreNames.contains(PROJECTS_STORE)) db.createObjectStore(PROJECTS_STORE)
      if (!db.objectStoreNames.contains(PINS_STORE)) db.createObjectStore(PINS_STORE)
      // Legacy stores are recreated empty if absent so reads never reject on
      // fresh installs; once migration completes they sit unused.
      if (!db.objectStoreNames.contains(LEGACY_PROJECT_STORE)) db.createObjectStore(LEGACY_PROJECT_STORE)
      if (!db.objectStoreNames.contains(LEGACY_MESSAGES_STORE)) db.createObjectStore(LEGACY_MESSAGES_STORE)
      if (!db.objectStoreNames.contains(LEGACY_TOOL_OUTPUTS_STORE)) db.createObjectStore(LEGACY_TOOL_OUTPUTS_STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function readKey<T>(db: IDBDatabase, store: string, key: string): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, "readonly")
    const req = tx.objectStore(store).get(key)
    req.onsuccess = () => resolve(req.result as T | undefined)
    req.onerror = () => reject(req.error)
  })
}

// Drop cache entries that don't carry an artifact. Pre-pin-feature entries
// were plain SVG strings; we discard them rather than try to back-fill —
// the user re-renders if they want a pinnable copy.
function sanitizeRendered<T extends { svg: string; artifact: object }>(raw: unknown): Record<string, T> {
  if (!raw || typeof raw !== "object") return {}
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>).filter(
      ([, v]) => v != null && typeof v === "object" && "svg" in v && "artifact" in v,
    ) as Array<[string, T]>,
  )
}

function sanitizeQuiltGrid(raw: unknown): Record<string, RenderedThumbnail[]> {
  if (!raw || typeof raw !== "object") return {}
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>)
      .filter(([, items]) => Array.isArray(items) && items.every(
        (it) => it != null && typeof it === "object" && "svg" in it && "artifact" in it,
      ))
      .map(([id, items]) => [id, items as RenderedThumbnail[]]),
  )
}

function sanitizeToolOutputs(raw: Partial<ToolOutputs> | undefined): ToolOutputs {
  const source = (raw ?? {}) as Partial<ToolOutputs> & {
    blockSvgs?: unknown; diagramSvgs?: unknown; quiltSvgs?: unknown; quiltGrids?: unknown
  }
  const merged: ToolOutputs = {
    ...EMPTY_TOOL_OUTPUTS,
    ...(raw ?? {}),
    blockSvgs: sanitizeRendered<RenderedBlock>(source.blockSvgs),
    diagramSvgs: sanitizeRendered<RenderedBlock>(source.diagramSvgs),
    quiltSvgs: sanitizeRendered<RenderedQuilt>(source.quiltSvgs),
    quiltGrids: sanitizeQuiltGrid(source.quiltGrids),
  }
  // Drop quilt SVGs with negative dimensions (legacy bug guard from when
  // rows/cols=0 combined with sashing produced width="-2").
  merged.quiltSvgs = Object.fromEntries(
    Object.entries(merged.quiltSvgs).filter(([, v]) => !/<svg[^>]*(?:width|height)="-/.test(v.svg))
  )
  return merged
}

function sanitizePhase(raw: unknown): Phase | null {
  return typeof raw === "string" && (PHASES as readonly string[]).includes(raw)
    ? (raw as Phase)
    : null
}

function hydrateDoc(raw: LoomDoc): LoomDoc {
  return {
    project: fromDict(raw.project as unknown as Record<string, unknown>),
    transcript: raw.transcript ?? [],
    toolOutputs: sanitizeToolOutputs(raw.toolOutputs),
    schemaVersion: raw.schemaVersion ?? 1,
    phase: sanitizePhase(raw.phase),
    ...(raw.kind ? { kind: raw.kind } : {}),
  }
}

export async function loadLoomDoc(): Promise<LoomDoc> {
  const db = await openDB()
  const direct = await readKey<LoomDoc>(db, DOC_STORE, KEY)
  if (direct) return hydrateDoc(direct)

  const [legacyProject, legacyMessages, legacyToolOutputs] = await Promise.all([
    readKey<Record<string, unknown>>(db, LEGACY_PROJECT_STORE, KEY),
    readKey<UIMessage[]>(db, LEGACY_MESSAGES_STORE, KEY),
    readKey<Partial<ToolOutputs>>(db, LEGACY_TOOL_OUTPUTS_STORE, KEY),
  ])

  const migrated: LoomDoc = {
    project: legacyProject ? fromDict(legacyProject) : defaultProject(),
    transcript: legacyMessages ?? [],
    toolOutputs: sanitizeToolOutputs(legacyToolOutputs),
    schemaVersion: 1,
    phase: null,
  }
  if (legacyProject || legacyMessages || legacyToolOutputs) {
    await saveLoomDoc(migrated)
  }
  return migrated
}

export async function saveLoomDoc(doc: LoomDoc): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DOC_STORE, "readwrite")
    tx.objectStore(DOC_STORE).put(doc, KEY)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

export async function clearLoomDoc(): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DOC_STORE, "readwrite")
    tx.objectStore(DOC_STORE).delete(KEY)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

// ── Saved projects (named, multiple) ────────────────────────────────────────
// Each ProjectDoc embeds a LoomDoc, so saving and opening preserves the
// rendered visualizations and pending menu rows along with project + transcript.
//
// Kind ("block" vs "quilt") lives on the embedded LoomDoc so it travels with
// the active session as well as with saved snapshots — see the comment on
// LoomDoc.kind for details.

export type DocKind = "block" | "quilt"

export interface ProjectDoc {
  id: string
  name: string
  createdAt: string
  updatedAt: string
  doc: LoomDoc
}

const CURRENT_DOC_ID_KEY = "loom:current_doc_id"

export function getCurrentDocId(): string | null {
  if (typeof window === "undefined") return null
  return localStorage.getItem(CURRENT_DOC_ID_KEY)
}

export function setCurrentDocId(id: string | null): void {
  if (typeof window === "undefined") return
  if (id === null) localStorage.removeItem(CURRENT_DOC_ID_KEY)
  else localStorage.setItem(CURRENT_DOC_ID_KEY, id)
}

export async function saveProjectDoc(doc: ProjectDoc): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PROJECTS_STORE, "readwrite")
    tx.objectStore(PROJECTS_STORE).put(doc, doc.id)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

function hydrateProjectDoc(raw: ProjectDoc): ProjectDoc | null {
  // Defensive guard: a partially-corrupt IDB record from an earlier dev
  // session may be missing `doc` or its `project` payload. Skip rather
  // than throw so a single bad entry doesn't break the whole switcher.
  if (!raw || !raw.doc || !raw.doc.project) return null
  return {
    ...raw,
    doc: hydrateDoc(raw.doc),
  }
}

// Derive a kind for display: prefer the explicit LoomDoc.kind when set
// (pin-materialized docs), otherwise infer from layout state — a project
// with rows × cols > 0 is a quilt, otherwise treat it as block-only.
export function deriveDocKind(doc: ProjectDoc): DocKind {
  if (doc.doc.kind) return doc.doc.kind
  const { rows, cols } = doc.doc.project.layout
  return rows > 0 && cols > 0 ? "quilt" : "block"
}

export async function loadProjectDoc(id: string): Promise<ProjectDoc | null> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PROJECTS_STORE, "readonly")
    const req = tx.objectStore(PROJECTS_STORE).get(id)
    req.onsuccess = () => {
      const raw = req.result as ProjectDoc | undefined
      if (!raw) { resolve(null); return }
      resolve(hydrateProjectDoc(raw))
    }
    req.onerror = () => reject(req.error)
  })
}

export async function listProjectDocs(): Promise<ProjectDoc[]> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PROJECTS_STORE, "readonly")
    const req = tx.objectStore(PROJECTS_STORE).getAll()
    req.onsuccess = () => {
      const docs: ProjectDoc[] = (req.result ?? [])
        .map(hydrateProjectDoc)
        .filter((d): d is ProjectDoc => d !== null)
      docs.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      resolve(docs)
    }
    req.onerror = () => reject(req.error)
  })
}

